import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import StarRating from "./StarRating";
import { toast } from "@/hooks/use-toast";
import { findProfaneWords } from "@/lib/profanityFilter";
import { checkReviewQuality, checkFieldQuality } from "@/lib/gibberishFilter";

interface PreceptorRatingFormProps {
  rotationTitle: string;
  open: boolean;
  onClose: () => void;
}

const PreceptorRatingForm = ({ rotationTitle, open, onClose }: PreceptorRatingFormProps) => {
  const [rating, setRating] = useState(0);
  const [name, setName] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [isRemote, setIsRemote] = useState(false);
  const [reviewText, setReviewText] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (rating === 0) {
      toast({ title: "Please select a rating", variant: "destructive" });
      return;
    }
    const fieldChecks: Array<[string, string]> = [
      [name, "Preceptor name"],
      [specialty, "Specialty"],
      [city, "City"],
      [state, "State"],
    ];
    for (const [val, label] of fieldChecks) {
      const fq = checkFieldQuality(val, label);
      if (!fq.ok) {
        toast({ title: "Please check your entry", description: fq.reason, variant: "destructive" });
        return;
      }
    }
    if (reviewText.trim()) {
      const quality = checkReviewQuality(reviewText);
      if (!quality.ok) {
        toast({
          title: "Please improve your review",
          description: quality.reason,
          variant: "destructive",
        });
        return;
      }
    }
    const offending = Array.from(new Set([
      ...findProfaneWords(reviewText),
      ...findProfaneWords(name),
      ...findProfaneWords(specialty),
    ]));
    if (offending.length > 0) {
      toast({
        title: "Please adjust your language",
        description: `Your submission contains vulgar or offensive language (${offending.slice(0, 3).join(", ")}${offending.length > 3 ? "…" : ""}). Please rephrase before posting.`,
        variant: "destructive",
      });
      return;
    }
    toast({
      title: "Preceptor review submitted!",
      description: `Your review for ${name} has been submitted.`,
    });
    setReviewText("");
    setRating(0);
    setName("");
    setSpecialty("");
    setCity("");
    setState("");
    setIsRemote(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 backdrop-blur-sm p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-xl"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-serif text-2xl text-card-foreground">Rate a Preceptor</h2>
                <p className="text-sm text-muted-foreground mt-1">{rotationTitle}</p>
              </div>
              <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Rating</Label>
                <StarRating rating={rating} size="lg" interactive onRatingChange={setRating} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="preceptor-name">Preceptor Name</Label>
                <Input
                  id="preceptor-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Dr. Sarah Chen"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="preceptor-specialty">Specialty</Label>
                <Input
                  id="preceptor-specialty"
                  value={specialty}
                  onChange={(e) => setSpecialty(e.target.value)}
                  placeholder="e.g. Cardiology"
                  required
                />
              </div>

              {/* Location */}
              <div className="space-y-2">
                <Label>Location</Label>
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    id="preceptor-remote-toggle"
                    checked={isRemote}
                    onChange={(e) => setIsRemote(e.target.checked)}
                    className="rounded border-input"
                  />
                  <Label htmlFor="preceptor-remote-toggle" className="text-sm font-normal cursor-pointer">Remote</Label>
                </div>
                {!isRemote && (
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="City"
                      required={!isRemote}
                    />
                    <Input
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      placeholder="State"
                      required={!isRemote}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="preceptor-review">Review (optional)</Label>
                <Textarea
                  id="preceptor-review"
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  placeholder="Share your experience with this preceptor..."
                  rows={3}
                  className="resize-none"
                />
              </div>

              <Button type="submit" className="w-full">
                Submit Preceptor Rating
              </Button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PreceptorRatingForm;
